import type { RootState } from '@/store';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { estimateStateClosingTaxes } from '@/utils/fees/stateClosingTaxes';
import type { CalculateClosingDisclosureProps } from '.';
import { calculateEnhancedInterestRate } from './rateIntegration';

const calculateHELOCInterestRate = (
  baseRate: number,
  creditScore: number,
  ltv: number,
  reduxState?: RootState
) => {
  // HELOC rates are typically variable and based on prime rate
  if (reduxState) {
    return calculateEnhancedInterestRate(
      reduxState,
      'HELOC',
      baseRate,
      creditScore,
      ltv
    );
  }
  return baseRate;
};

export const calculateHELOCClosingDisclosure = (
  props: CalculateClosingDisclosureProps & Record<string, any>,
  reduxState?: RootState
) => {
  const {
    purchasePrice: purchasePriceProp,
    offerPrice,
    listPrice,
    downPayment,
    creditScore,
    propertyType,
    occupancy,
    state: propertyState,
    county,
    city,
    zipCode,
    propertyTaxRate,
    homeownersInsuranceRate,
    hoaFees,
    currentLoanBalance,
    requestedCreditLine,
    marketInterestRate = 7.5, // HELOC rates are typically variable
  } = props as any;

  // HELOC specific calculations
  const helocCreditLine = requestedCreditLine || 100000; // Default $100k line
  // Callers pass offerPrice/listPrice (the home's value); purchasePrice
  // was never set, so every value-based figure came out NaN.
  const purchasePrice =
    Number(purchasePriceProp ?? offerPrice ?? listPrice) || 0;
  const loanAmount = purchasePrice - (downPayment || 0);
  // Price on combined LTV: existing first mortgage plus this new lien.
  const existingBalance = Number(currentLoanBalance);
  const ltv = purchasePrice
    ? ((Number.isFinite(existingBalance) && existingBalance > 0
        ? existingBalance
        : loanAmount) +
        helocCreditLine) /
      purchasePrice
    : 0.8;
  const helocInterestRate = calculateHELOCInterestRate(
    marketInterestRate || 7.5,
    Number(creditScore) || 700,
    Number(ltv) || 0.8,
    reduxState
  );

  // HELOC terms
  const drawPeriod = 120; // 10 year draw period
  const repaymentPeriod = 240; // 20 year repayment period
  const totalTerm = drawPeriod + repaymentPeriod;

  // HELOC specific fees (typically lower than traditional refinance)
  const appraisalFeeBase = 500; // Lower appraisal fee for HELOC
  const processingFeeBase = 500; // Lower processing fee
  const underwritingFee = 300; // Lower underwriting fee
  const applicationFeeBase = 100; // Lower application fee

  // HELOC specific fees
  const annualFee = 50; // Annual maintenance fee
  const inactivityFee = 25; // Fee if no activity for 12 months

  // Title and settlement fees
  const titleSettlementAgentFee = 300; // Lower for HELOC
  const titleLendersTitleInsuranceBase = (helocCreditLine * 0.003).toFixed(2); // Lower rate for HELOC

  // Recording fees
  const countyRecordingFees = 50;
  const stateRecordingFees = 50;
  const recordingTitle = 50;
  const recordingMortgage = 150;
  const recordingDeedBase = 50;

  // Transfer taxes (typically not applicable for HELOC)
  const transferTaxes = 0;
  // A new lien still owes the borrower's state loan taxes (e.g. Florida
  // doc stamps + intangible tax) — see utils/fees/stateClosingTaxes.
  const stateTaxes = estimateStateClosingTaxes({
    zipCode,
    price: purchasePrice,
    // Taxed on the recorded credit line.
    loanAmount: helocCreditLine,
    transaction: 'refinance',
  });
  const mortgageTaxes = stateTaxes.mortgageTaxes;

  // Prepaid costs (minimal for HELOC)
  const prepaidsPropertyTaxes = 0; // No prepaid taxes for HELOC
  const prepaidsHomeownersInsurance = 0; // No prepaid insurance for HELOC
  const prepaidInterest = 0; // No prepaid interest for HELOC

  // Interest-only payment during the draw period if the full line is drawn
  // (showing $0/mo read as a broken estimate).
  const monthlyPayment = (helocCreditLine * helocInterestRate) / 12 / 100;
  const monthlyPrincipalInterest = monthlyPayment;

  // Lender cost totals
  const lenderCostTotals =
    appraisalFeeBase +
    processingFeeBase +
    underwritingFee +
    applicationFeeBase +
    annualFee +
    titleSettlementAgentFee +
    parseFloat(titleLendersTitleInsuranceBase) +
    countyRecordingFees +
    stateRecordingFees +
    recordingTitle +
    recordingMortgage +
    recordingDeedBase +
    transferTaxes +
    mortgageTaxes;

  // Prepaid totals
  const prepaidTotals =
    prepaidsPropertyTaxes + prepaidsHomeownersInsurance + prepaidInterest;

  // Total closing costs
  const totalClosingCosts = lenderCostTotals + prepaidTotals;

  // Cash to close (minimal for HELOC)
  const cashToClose = totalClosingCosts;

  return {
    type: USER_LOAN_PROGRAM.HELOC,
    loanAmount: helocCreditLine,
    interestRate: helocInterestRate,
    monthlyPayment,
    monthlyPrincipalInterest,
    loanTerm: totalTerm,
    drawPeriod,
    repaymentPeriod,
    appraisalFeeBase,
    processingFeeBase,
    underwritingFee,
    applicationFeeBase,
    annualFee,
    inactivityFee,
    titleSettlementAgentFee,
    titleLendersTitleInsuranceBase,
    countyRecordingFees,
    stateRecordingFees,
    recordingTitle,
    recordingMortgage,
    recordingDeedBase,
    transferTaxes,
    mortgageTaxes,
    stateTaxLines: stateTaxes.lines,
    stateTaxState: stateTaxes.state,
    prepaidsPropertyTaxes,
    prepaidsHomeownersInsurance,
    prepaidInterest,
    lenderCostTotals,
    prepaidTotals,
    totalClosingCosts,
    cashToClose,
    // HELOC specific properties
    minimumOwnersTitle: 1650,
    maximumOwnersTitle: 5850,
    // The borrower already owns the home: no owner's title policy.
    optionalOwnersTitleInsurance: 0,
    minimumInspectionFee: 650,
    maximumInspectionFee: 4500,
    homeInspectionFee: Math.min(Math.max(500, 650), 4500),
    // Refinance specific properties
    currentLoanBalance,
    requestedCreditLine: helocCreditLine,
  };
};
